import { ChangeEvent, FC, useMemo, useState } from "react";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import Fuse from "fuse.js";
import { filter, includes, map, some } from "lodash";
import { Link } from "react-router-dom";

import { Button, RoyaltyPayorImage } from "components";
import CanGetValuationIcon from "components/RoyaltyPayorImage/CanGetValuationIcon";
import { useRoyaltyPayors } from "services/royaltyPayor";
import theme from "theme";
import { textFieldPurpleStyles } from "theme/constants";

import * as addRoyaltyAccountRoutes from "../routes";
import StepContainer from "./StepContainer";

const useStyles = makeStyles({
  stepTitle: {
    color: theme.palette.text.primary,
    fontSize: "2.2rem",
    fontWeight: theme.typography.fontWeightMedium,
  },
  searchInputRoot: textFieldPurpleStyles,
  royaltyPayorChoicesContainer: {
    borderBottom: `1px solid ${theme.custom.palette.gray.main}`,
    maxHeight: "40vh",
    minHeight: "413px",
    overflowX: "hidden",
    padding: theme.spacing(0.5),
  },
  connectRoyaltyPayorImageContainer: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  valuationKindContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(1)}px`,
  },
});

const SearchRoyaltyPayors: FC = () => {
  const classes = useStyles();
  const { data: royalty_payors } = useRoyaltyPayors();
  const [query, setQuery] = useState("");
  const royaltyPayorsCanAdd = filter(royalty_payors, royaltyPayor =>
    some(
      royaltyPayor.permissions.map(permission => permission.codename),
      permission => includes(["get_valuation", "get_credentials"], permission),
    ),
  );

  const fuse = useMemo(
    () =>
      new Fuse(royaltyPayorsCanAdd, {
        keys: ["display_name", "name"],
      }),
    [royaltyPayorsCanAdd],
  );

  const royaltyPayorsMatchQuery = query
    ? fuse.search(query).map(result => result.item)
    : royaltyPayorsCanAdd;

  const royaltyPayorsCanGetValuation = filter(
    royaltyPayorsMatchQuery,
    royaltyPayor =>
      some(royaltyPayor.permissions, { codename: "get_valuation" }),
  );

  const royaltyPayorsCanGetCredentials = filter(
    royaltyPayorsMatchQuery,
    royaltyPayor =>
      some(royaltyPayor.permissions, { codename: "get_credentials" }),
  );

  const connectableRoyaltyPayors = royaltyPayorsCanGetValuation.concat(
    royaltyPayorsCanGetCredentials,
  );

  return (
    <>
      <StepContainer>
        <Typography variant="h1" className={classes.stepTitle} gutterBottom>
          Search For Your Royalty Account
        </Typography>
        <Box pb={2}>
          <TextField
            autoFocus
            id="search-royalty-payors"
            placeholder="ASCAP, Sony, Tunecore, etc."
            label="Search"
            fullWidth
            className={classes.searchInputRoot}
            value={query}
            onChange={(ev: ChangeEvent<HTMLInputElement>) =>
              setQuery(ev.target.value)
            }
            margin="dense"
            variant="outlined"
          />
        </Box>
        <Box className={classes.royaltyPayorChoicesContainer}>
          {!!royaltyPayorsMatchQuery.length && (
            <Grid container spacing={2}>
              {map(connectableRoyaltyPayors, royaltyPayor => {
                const { name } = royaltyPayor;
                const connectRoute = some(royaltyPayor.permissions, {
                  codename: "get_valuation",
                })
                  ? addRoyaltyAccountRoutes.connectGetValuationRoyaltyPayorAccount(
                      name,
                    )
                  : addRoyaltyAccountRoutes.connectGetCredentialsRoyaltyPayorAccount(
                      name,
                    );
                return (
                  <Grid item sm={4} xs={6} key={name}>
                    <Link
                      data-attr="connect-royalty-payor-link"
                      to={connectRoute}
                    >
                      <Card
                        className={classes.connectRoyaltyPayorImageContainer}
                      >
                        <RoyaltyPayorImage
                          royaltyPayor={royaltyPayor}
                          royaltyPayorLogoMaxHeight="40px"
                        />
                      </Card>
                    </Link>
                  </Grid>
                );
              })}
            </Grid>
          )}
          {!!query && (
            <Box mt={2} mb={2}>
              <Box mb={2}>
                {!!royaltyPayorsMatchQuery.length ? (
                  <Typography>
                    If the Royalty Account you are searching for is not
                    displayed, you can submit details of your Royalty Account by
                    clicking the button below.
                  </Typography>
                ) : (
                  <Typography>
                    We could not find any Royalty Accounts matching &quot;
                    {query}&quot;. You can submit details of your Royalty
                    Account by clicking the button below.
                  </Typography>
                )}
              </Box>
              <Button
                data-attr="submit-other-royalty-account-button"
                fullWidth
                component={Link}
                variant="contained"
                to={addRoyaltyAccountRoutes.otherSourceSubmission()}
              >
                Submit Other Royalty Account
              </Button>
            </Box>
          )}
        </Box>
        <Box className={classes.valuationKindContainer}>
          <Box>
            <CanGetValuationIcon canGetValuation showLabel />
          </Box>
          <Box>
            <CanGetValuationIcon canGetValuation={false} showLabel />
          </Box>
        </Box>
      </StepContainer>
    </>
  );
};

export default SearchRoyaltyPayors;
